import { GetStaticPropsContext } from "next";
import { gql } from "@apollo/client";
import * as MENUS from "@/constants/menus";
import {
  BlogInfoFragment,
  MenuItemFragment,
  NewMenuItemFragment,
} from "@/fragments";
import { Container, Main, Heading } from "@/components/atoms";
import { Footer } from "@/components/organisms";
import { getNextStaticProps } from "@faustwp/core";
import { SEO } from "@/components/molecules";
import { useTranslate } from "@/hooks/useTranslate";
import { Locale } from "@/types";
import { GLOBAL_REVALIDATE } from "@/constants/revalidate";
import DynamicHeader from "@/components/organisms/DynamicHeader";
import CustomButton from "@/components/molecules/CustomButton";
import { ERROR_404_BUTTON_V2 } from "@/i18n/translations";

export default function Page({ data, locale }: { data: any; locale: Locale }) {
  const { translate } = useTranslate();

  const siteTitle = data?.generalSettings?.title;
  const siteDescription = data?.generalSettings?.description;
  const footerMenu = data?.footerMenuItems?.nodes ?? [];
  const wkNavigationMenuItems = data?.wkNavigationMenuItems?.nodes ?? [];

  return (
    <>
      <SEO title={siteTitle} description={siteDescription} />
      <DynamicHeader
        title={siteTitle}
        description={siteDescription}
        wkNavigationMenuItems={wkNavigationMenuItems}
      />
      <Main className="relative flex flex-col items-center justify-center bg-wk-espresso-100 text-wk-primary-text">
        <Container tagName="section">
          <div className="mt-wk-space-20 flex flex-col items-center justify-center text-center md:mt-wk-space-120">
            {locale === "en" ? (
              <>
                <Heading level="h1" className="wk-h1 mb-wk-space-20">
                  Well, this bytes.
                </Heading>
                <p className="mb-wk-space-20 max-w-3xl">
                  We lost this page, but we’ll never lose your passwords.
                </p>
              </>
            ) : (
              <Heading level="h1" className="wk-h1 mb-wk-space-20">
                404
              </Heading>
            )}
            <CustomButton
              variant="aquamarine"
              text={translate(ERROR_404_BUTTON_V2)}
              href={`${locale === "en" ? "" : "/" + locale}/`}
              size="lg"
              align="center"
            />
          </div>
          <div className="mt-8 flex w-full justify-center">
            <img
              src="/images/404.svg"
              alt="404 illustration"
              className="pointer-events-none h-auto w-full max-w-5xl select-none object-contain"
              draggable="false"
              loading="lazy"
            />
          </div>
        </Container>
      </Main>
      <Footer title={siteTitle} menuItems={footerMenu} />
    </>
  );
}

Page.query = gql`
  ${BlogInfoFragment}
  ${MenuItemFragment}
  ${NewMenuItemFragment}
  query GetPageData(
    $footerLocation: MenuLocationEnum
    $locale: LanguageCodeFilterEnum = EN
    $wkNavigationLocation: MenuLocationEnum
  ) {
    generalSettings {
      ...BlogInfoFragment
    }
    footerMenuItems: menuItems(
      where: { location: $footerLocation, language: $locale }
      first: ${MENUS.NUMBER_OF_MENU_ITEMS}
    ) {
      nodes {
        ...MenuItemFragment
      }
    }
    wkNavigationMenuItems: menuItems(
      where: { location: $wkNavigationLocation, language: $locale }
      first: ${MENUS.NUMBER_OF_MENU_ITEMS}
    ) {
      nodes {
        ...NewMenuItemFragment
      }
    }
  }
`;

Page.variables = ({ locale }: { locale?: string }) => {
  return {
    footerLocation: MENUS.newFooterLocation(locale as Locale),
    wkNavigationLocation: MENUS.wkNavigationMenuLocation(locale as Locale),
  };
};

export async function getStaticProps(context: GetStaticPropsContext) {
  return getNextStaticProps(context, {
    Page: {
      ...Page,
      variables: () => {
        const locale = context.locale?.toUpperCase().replace("-", "_");
        return {
          ...Page.variables({ locale: context.locale }),
          locale,
        };
      },
    },
    revalidate: GLOBAL_REVALIDATE,
  });
}
